'use client';

import { type ReactNode, useState } from 'react';

import classnames from 'classnames';

import { Account } from '../account';
import { Avatar } from '../avatar';
import type { AvatarSize } from '../avatar/Avatar.types';
import type { BaseComponentProps } from '../base-component.types';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';

import styles from './UserMenu.module.scss';

type UserMenuProps = BaseComponentProps & {
  username?: string;
  email?: string;
  avatarUrl?: string;
  avatarSize?: AvatarSize;
  /**
   * Menu entries, typically UserMenuItem. Pass a function to receive a
   * `close` callback: entries that navigate away want it, entries that toggle
   * something in place or open a dialog do not.
   */
  children?: ReactNode | ((close: () => void) => ReactNode);
};

/**
 * The signed-in user's avatar, opening a popover with their identity and a
 * caller-supplied list of actions.
 */
function UserMenu({
  className,
  username = 'username',
  email = 'email',
  avatarUrl,
  avatarSize = 'sm',
  children,
  testId = 'ui-user-menu',
}: UserMenuProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover
      placement="bottom-end"
      open={open}
      onOpenChange={setOpen}
      dismissOnAncestorScroll
    >
      <PopoverTrigger
        asChild={true}
        onClick={() => setOpen(true)}
        className={styles['trigger']}
        data-testid={testId}
      >
        <Avatar src={avatarUrl} size={avatarSize} alt={`Menú de ${username}`} />
      </PopoverTrigger>
      <PopoverContent className={classnames(styles['options'], className)}>
        <Account
          className={styles['account']}
          username={username}
          email={email}
          src={avatarUrl}
          size="sm"
        />
        {typeof children === 'function'
          ? children(() => setOpen(false))
          : children}
      </PopoverContent>
    </Popover>
  );
}

export { UserMenu, type UserMenuProps };
